import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { UserService } from './user.service';
import { environment } from '../environments/environment';
import { Observable, of, switchMap, filter, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { callApi } from '../state/actions';
import { ApiState } from '../state/reducer'; // import your reducer type

// Define the AppState to type the store
export interface AppState {
  api: ApiState;
}

@Injectable({ providedIn: 'root' })
export class ReduxApiHandlerService {
  private apiUrl = environment.apiUrl;
  private trackedEndpoints: string[] = [];

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private store: Store<AppState> // typed store
  ) {}

  /** Generic API call */
  callApi(method: string, endpoint: string, payload: any = {}): Observable<HttpResponse<any>> {
    const token = this.userService.getAccessToken();

    if (!this.trackedEndpoints.includes(endpoint)) {
      this.trackedEndpoints.push(endpoint);
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    const options = { headers, observe: 'response' as const };

    switch (method.toUpperCase()) {
      case 'POST':
        return this.http.post<any>(`${this.apiUrl}${endpoint}`, payload, options);
      case 'PUT':
        return this.http.put<any>(`${this.apiUrl}${endpoint}`, payload, options);
      case 'DELETE':
        return this.http.delete<any>(`${this.apiUrl}${endpoint}`, options);
      default:
        return this.http.get<any>(`${this.apiUrl}${endpoint}`, options);
    }
  }

  /** Fetch data from store first (cache-first) */
  fetchWithCache<T>(endpoint: string, payload: any = {}, forceRefresh = false): Observable<T> {
  // Track the endpoint
  if (!this.trackedEndpoints.includes(endpoint)) {
    this.trackedEndpoints.push(endpoint);
  }

  return this.store.select(state => state.api.data[endpoint]).pipe(
    take(1),
    switchMap(cachedData => {
      if (cachedData && !forceRefresh) {
        return of(cachedData) as Observable<T>; // return cached data
      } else {
        // Dispatch API call regardless if forceRefresh is true or data not present
        this.store.dispatch(callApi({ method: 'GET', endpoint, payload, key: endpoint }));

        return this.store.select(state => state.api.data[endpoint]).pipe(
          filter(d => Boolean(d)),
          take(1)
        ) as Observable<T>;
      }
    })
  );
}

/** Manual refresh for all endpoints */
forceRefreshAll(): void {
  this.trackedEndpoints.forEach(endpoint => {
    // Force refresh by dispatching API again
    this.store.dispatch(callApi({ method: 'GET', endpoint, payload: {}, key: endpoint }));
  });
}
}
